import { useState, useCallback } from "react";
import type {
	FlowNode,
	FlowConnection,
	ChatMessage,
	Lead,
	NodeType,
} from "./flow-types";

let nextId = 1;
export function genId(prefix = "node") {
	return `${prefix}_${nextId++}_${Date.now().toString(36)}`;
}

const DEFAULT_NODES: FlowNode[] = [
	{
		id: "node_start",
		type: "greeting",
		label: "Welcome!",
		x: 300,
		y: 60,
		config: {
			message: "Hey there! Welcome to our site. How can I help you today?",
		},
	},
	{
		id: "node_ask",
		type: "question",
		label: "What brings you here?",
		x: 300,
		y: 200,
		config: {
			message: "Are you looking for pricing, a demo, or just exploring?",
		},
	},
	{
		id: "node_ai",
		type: "ai-response",
		label: "Smart Reply",
		x: 120,
		y: 340,
		config: {
			prompt:
				"Based on the user's response, provide a helpful and friendly reply. Reference their specific needs.",
		},
	},
	{
		id: "node_capture",
		type: "lead-capture",
		label: "Get Contact Info",
		x: 480,
		y: 340,
		config: {
			message: "I'd love to help further! Could you share your name and email?",
			field: "email",
		},
	},
	{
		id: "node_qualify",
		type: "qualify",
		label: "Score Lead",
		x: 480,
		y: 480,
		config: {
			qualifyScore: 70,
			condition: "interested in demo OR pricing",
		},
	},
	{
		id: "node_condition",
		type: "condition",
		label: "High Intent?",
		x: 300,
		y: 600,
		config: {
			condition: "lead_score >= 70",
			trueLabel: "Yes - Hot Lead",
			falseLabel: "No - Nurture",
		},
	},
	{
		id: "node_handoff",
		type: "handoff",
		label: "Connect to Sales",
		x: 140,
		y: 740,
		config: {
			handoffEmail: "sales@company.com",
			message:
				"Great news! Let me connect you with our sales team right away.",
		},
	},
	{
		id: "node_end",
		type: "end",
		label: "Thanks & Goodbye",
		x: 460,
		y: 740,
		config: {
			message:
				"Thanks for chatting! We'll follow up with some helpful resources.",
		},
	},
];

const DEFAULT_CONNECTIONS: FlowConnection[] = [
	{ id: "conn_1", from: "node_start", to: "node_ask" },
	{ id: "conn_2", from: "node_ask", to: "node_ai", label: "Exploring" },
	{
		id: "conn_3",
		from: "node_ask",
		to: "node_capture",
		label: "Pricing/Demo",
	},
	{ id: "conn_4", from: "node_capture", to: "node_qualify" },
	{ id: "conn_5", from: "node_qualify", to: "node_condition" },
	{ id: "conn_6", from: "node_ai", to: "node_condition" },
	{
		id: "conn_7",
		from: "node_condition",
		to: "node_handoff",
		label: "Hot Lead",
	},
	{ id: "conn_8", from: "node_condition", to: "node_end", label: "Nurture" },
];

const SAMPLE_LEADS: Lead[] = [
	{
		id: "lead_1",
		name: "Sarah Chen",
		email: "sarah@techcorp.io",
		score: 92,
		status: "qualified",
		capturedAt: new Date(Date.now() - 3600000 * 2),
		tags: ["demo-request", "enterprise"],
		messages: [
			{
				id: "m1",
				role: "bot",
				content: "Hey there! Welcome to our site. How can I help you today?",
				timestamp: new Date(Date.now() - 3600000 * 2),
			},
			{
				id: "m2",
				role: "user",
				content: "I'd like to see a demo for my team of 50+",
				timestamp: new Date(Date.now() - 3600000 * 2 + 30000),
			},
			{
				id: "m3",
				role: "bot",
				content:
					"Awesome! For a team that size, our Enterprise plan would be perfect. Let me get your details!",
				timestamp: new Date(Date.now() - 3600000 * 2 + 60000),
			},
		],
	},
	{
		id: "lead_2",
		name: "Mike Johnson",
		email: "mike@startup.co",
		score: 75,
		status: "new",
		capturedAt: new Date(Date.now() - 3600000),
		tags: ["pricing", "startup"],
		messages: [
			{
				id: "m4",
				role: "bot",
				content: "Hey there! Welcome to our site. How can I help you today?",
				timestamp: new Date(Date.now() - 3600000),
			},
			{
				id: "m5",
				role: "user",
				content: "What's the pricing for a small team?",
				timestamp: new Date(Date.now() - 3600000 + 20000),
			},
		],
	},
	{
		id: "lead_3",
		name: "Emma Wilson",
		email: "emma@agency.com",
		score: 45,
		status: "contacted",
		capturedAt: new Date(Date.now() - 7200000),
		tags: ["exploring"],
		messages: [
			{
				id: "m6",
				role: "bot",
				content: "Hey there! Welcome to our site. How can I help you today?",
				timestamp: new Date(Date.now() - 7200000),
			},
			{
				id: "m7",
				role: "user",
				content: "Just browsing for now",
				timestamp: new Date(Date.now() - 7200000 + 15000),
			},
		],
	},
	{
		id: "lead_4",
		name: "Alex Rivera",
		email: "alex@bigco.com",
		score: 88,
		status: "converted",
		capturedAt: new Date(Date.now() - 86400000),
		tags: ["demo-request", "enterprise", "urgent"],
		messages: [],
	},
	{
		id: "lead_5",
		name: "Jordan Lee",
		email: "jordan@freelance.me",
		score: 30,
		status: "lost",
		capturedAt: new Date(Date.now() - 172800000),
		tags: ["exploring", "freelancer"],
		messages: [],
	},
];

const CHAT_HISTORY: ChatMessage[] = [
	{
		id: "ch_1",
		role: "system",
		content: "Chat session started",
		timestamp: new Date(Date.now() - 600000),
	},
	{
		id: "ch_2",
		role: "bot",
		content:
			"Hey there! Welcome to our site. How can I help you today?",
		timestamp: new Date(Date.now() - 590000),
		nodeId: "node_start",
	},
	{
		id: "ch_3",
		role: "user",
		content: "I'm interested in your pricing plans",
		timestamp: new Date(Date.now() - 550000),
	},
	{
		id: "ch_4",
		role: "bot",
		content:
			"Great question! We have three plans: Starter ($29/mo), Pro ($79/mo), and Enterprise (custom). Based on your needs, I can help find the right fit. How large is your team?",
		timestamp: new Date(Date.now() - 540000),
		nodeId: "node_ai",
	},
	{
		id: "ch_5",
		role: "user",
		content: "We have about 20 people. The Pro plan sounds interesting.",
		timestamp: new Date(Date.now() - 500000),
	},
	{
		id: "ch_6",
		role: "bot",
		content:
			"The Pro plan is perfect for teams of 10-50! It includes unlimited chatbot flows, AI responses, and priority support. Would you like to book a quick demo to see it in action?",
		timestamp: new Date(Date.now() - 490000),
		nodeId: "node_ai",
	},
];

export function useFlowStore() {
	const [nodes, setNodes] = useState<FlowNode[]>(DEFAULT_NODES);
	const [connections, setConnections] =
		useState<FlowConnection[]>(DEFAULT_CONNECTIONS);
	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
	const [leads, setLeads] = useState<Lead[]>(SAMPLE_LEADS);
	const [chatMessages, setChatMessages] =
		useState<ChatMessage[]>(CHAT_HISTORY);

	const addNode = useCallback(
		(type: NodeType, x: number, y: number) => {
			const node: FlowNode = {
				id: genId("node"),
				type,
				label: type.charAt(0).toUpperCase() + type.slice(1).replace("-", " "),
				x,
				y,
				config: {},
			};
			setNodes((prev) => [...prev, node]);
			return node;
		},
		[]
	);

	const updateNode = useCallback(
		(id: string, updates: Partial<FlowNode>) => {
			setNodes((prev) =>
				prev.map((n) => (n.id === id ? { ...n, ...updates } : n))
			);
		},
		[]
	);

	const removeNode = useCallback(
		(id: string) => {
			setNodes((prev) => prev.filter((n) => n.id !== id));
			setConnections((prev) =>
				prev.filter((c) => c.from !== id && c.to !== id)
			);
			if (selectedNodeId === id) setSelectedNodeId(null);
		},
		[selectedNodeId]
	);

	const addConnection = useCallback(
		(from: string, to: string, label?: string) => {
			const conn: FlowConnection = {
				id: genId("conn"),
				from,
				to,
				label,
			};
			setConnections((prev) => [...prev, conn]);
			return conn;
		},
		[]
	);

	const removeConnection = useCallback((id: string) => {
		setConnections((prev) => prev.filter((c) => c.id !== id));
	}, []);

	const addChatMessage = useCallback(
		(role: ChatMessage["role"], content: string, nodeId?: string) => {
			const msg: ChatMessage = {
				id: genId("msg"),
				role,
				content,
				timestamp: new Date(),
				nodeId,
			};
			setChatMessages((prev) => [...prev, msg]);
			return msg;
		},
		[]
	);

	const selectedNode = nodes.find((n) => n.id === selectedNodeId) || null;

	return {
		nodes,
		connections,
		selectedNodeId,
		selectedNode,
		leads,
		chatMessages,
		setNodes,
		setConnections,
		setSelectedNodeId,
		setLeads,
		addNode,
		updateNode,
		removeNode,
		addConnection,
		removeConnection,
		addChatMessage,
	};
}
